'use client';

import Link from 'next/link';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { register } from '@/lib/api/auth';
import { useAuth } from '@/lib/context/AuthContext';
import { AuthShell } from '@/components/auth/AuthShell';
import { Button, Field, Input, PasswordInput } from '@/components/ui';

const ErrorIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
);

export default function RegisterPage() {
  const router = useRouter();
  const { setSession, user, loading: authLoading } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const mismatch = confirm.length > 0 && password !== confirm;

  useEffect(() => {
    if (!authLoading && user) {
      router.replace('/home');
    }
  }, [user, authLoading, router]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (mismatch) {
      setError('Kata sandi tidak cocok');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const u = await register({
        name,
        email,
        password,
        password_confirmation: confirm,
      });
      if (!u.token) throw new Error('Token tidak ditemukan dari server');
      setSession(u.token, u);
      router.push('/home');
    } catch (err: any) {
      setError(err?.message || 'Sign up gagal');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthShell
      headline="Satu akun untuk semua rapat instansi."
      sub="Daftar sekali, akses dari mana saja dengan keamanan tingkat instansi."
    >
      <h1 className="mx-h1" style={{ fontSize: 28, marginBottom: 'var(--mx-sp-2)' }}>Buat akun</h1>
      <p className="mx-body" style={{ marginBottom: 24 }}>Daftar dengan email instansi Anda.</p>

      <form onSubmit={handleSubmit} noValidate>
        <Field label="Nama lengkap" htmlFor="nm">
          <Input id="nm" autoComplete="name" placeholder="Nama lengkap" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>

        <div style={{ height: 'var(--mx-sp-4)' }} />

        <Field label="Email" htmlFor="em">
          <Input id="em" type="email" autoComplete="email" placeholder="nama@instansi.go.id" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </Field>

        <div style={{ height: 'var(--mx-sp-4)' }} />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--mx-sp-3)' }}>
          <div style={{ flex: '1 1 160px' }}>
            <Field label="Sandi" htmlFor="p1">
              <PasswordInput id="p1" autoComplete="new-password" placeholder="Kata sandi" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Field>
          </div>
          <div style={{ flex: '1 1 160px' }}>
            <Field label="Konfirmasi" htmlFor="p2">
              <PasswordInput id="p2" autoComplete="new-password" placeholder="Ulangi sandi" value={confirm} onChange={(e) => setConfirm(e.target.value)} invalid={mismatch} required />
            </Field>
          </div>
        </div>

        {(mismatch || error) ? (
          <p className="mx-hint mx-hint--error" role="alert" style={{ marginTop: 14 }}>{ErrorIcon}{mismatch ? 'Kata sandi tidak cocok' : error}</p>
        ) : null}

        <div style={{ height: 22 }} />

        <Button type="submit" variant="primary" size="lg" block loading={loading}>Daftar</Button>
      </form>

      <p className="mx-caption" style={{ textAlign: 'center', marginTop: 22 }}>
        Sudah punya akun?{' '}
        <Link href="/login" style={{ color: 'var(--mx-accent)', fontWeight: 600, textDecoration: 'none' }}>Masuk</Link>
      </p>
    </AuthShell>
  );
}
